import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import { Bell, Check, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";

const TYPE_DOT = {
  refund_initiated: "var(--info)",
  refund_done: "var(--success)",
  refund_rejected: "var(--danger)",
  discount_requested: "#f59e0b",
};

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 604800) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

export default function NotificationBell({ compact = false }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const btnRef = useRef(null);
  const popRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications?limit=30")
      .then((r) => { setItems(r.data.notifications || []); setUnread(r.data.unread_count || 0); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target)) return;
      if (popRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const openNotif = async (n) => {
    if (!n.read) {
      await api.post(`/notifications/${n.id}/read`).catch(() => {});
      setUnread((u) => Math.max(0, u - 1));
      setItems((arr) => arr.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    await api.post("/notifications/read-all").catch(() => {});
    setUnread(0);
    setItems((arr) => arr.map((x) => ({ ...x, read: true })));
  };

  // Fixed-position popover portaled to body so the sidebar's overflow:hidden
  // (and hover-collapse) can never clip or unmount it.
  const popStyle = compact
    ? { top: 56, right: 12 }
    : { top: 64, left: 76 };

  const popover = open ? ReactDOM.createPortal(
    <div className="notif-popover" style={popStyle} ref={popRef} data-testid="notification-popover">
      <div className="notif-popover-head">
        <span>Notifications</span>
        {unread > 0 && (
          <button className="notif-markall" onClick={markAll} data-testid="notification-mark-all">
            <CheckCheck size={13} style={{ marginRight: 3 }} />Mark all read
          </button>
        )}
      </div>
      <div className="notif-list">
        {items.length === 0 ? (
          <div className="notif-empty" data-testid="notification-empty">You're all caught up.</div>
        ) : (
          items.map((n) => (
            <button key={n.id} type="button"
              className={`notif-item ${n.read ? "" : "unread"}`}
              onClick={() => openNotif(n)}
              data-testid={`notification-item-${n.id}`}>
              <span className="notif-dot" style={{ background: TYPE_DOT[n.type] || "var(--text-muted)" }} />
              <span className="notif-body">
                <span className="notif-title">{n.title}</span>
                <span className="notif-text">{n.body}</span>
                <span className="notif-time">{timeAgo(n.created_at)}</span>
              </span>
              {!n.read && <Check size={13} className="notif-check" />}
            </button>
          ))
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div className="notif-wrap">
      <button
        type="button"
        ref={btnRef}
        className="logout-btn notif-bell-btn"
        onClick={() => { setOpen((o) => !o); if (!open) load(); }}
        title="Notifications"
        data-testid="notification-bell"
      >
        <Bell size={compact ? 16 : 15} />
        {unread > 0 && (
          <span className="notif-badge" data-testid="notification-unread-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>
      {popover}
    </div>
  );
}
